import {
  ConditionalRuleValidator,
  decidePattern,
  IFormSchemaV2,
  isInputElement,
} from "@graffer-inc/form-schema";
import { CloudGovFormSchemaAccessor } from "./accessor-helper";
import type { ApplicantTypeLiterals } from "../../../application/applicant";
import {
  mojCertificateContainedAttributes,
  myNumberContainedAttributes,
  ReservedApplicantIdHelper,
  toMojContainAttrLabel,
  toMyNumberContainAttrLabel,
} from "../applicant-schema/applicant-reserved-ids";
import type { ApplicantSchemaKeys } from "../applicant-schema-keys";

export class SmartApplyConditionalRuleValidator extends ConditionalRuleValidator {
  constructor(
    schema: IFormSchemaV2,
    private readonly applicantSchemaKeys: ApplicantSchemaKeys,
  ) {
    super(schema);
  }

  validate(): string[] {
    const results = super.validate();
    this.append(results, this.applicantSectionRules());
    this.append(results, this.applicantSectionNotAllHidden());
    this.append(results, this.userAreaNotContainApplicantElements());
    this.append(results, this.applicantEmail());
    return results;
  }

  assertion(): void {
    super.assertion();
    this.assertError([
      this.applicantSectionRules(),
      this.applicantSectionNotAllHidden(),
      this.userAreaNotContainApplicantElements(),
      // 他に問題ないことを確認したら追加する
      // this.applicantEmail()
    ]);
  }

  /**
   * 申請者セクションに対応する条件付きルールが存在すること
   */
  applicantSectionRules() {
    if (!this.accessor.applicantSection) {
      return "申請者セクションがありません";
    }
    const reverse = this.accessor.supportedApplicantTypes.reverse();
    if (
      reverse.length > this.accessor.conditionalRuleSet!.conditionalRules.length
    ) {
      return "条件付きルールの数が不足してます";
    }
    const ruleLength =
      this.accessor.conditionalRuleSet!.conditionalRules.length;
    for (let i = 0; i < reverse.length; i++) {
      const rule =
        this.accessor.conditionalRuleSet!.conditionalRules[ruleLength - 1 - i];
      if (
        rule.if.conditions.length !== 1 ||
        rule.if.conditions[0].match.const !== reverse[i]
      ) {
        return `申請者の種別 ${reverse[i]}に対して、期待する位置に条件付きルールがありませんでした`;
      }

      const supportedType = reverse[i];
      if (!rule || rule.if.conditions[0].match.const !== supportedType) {
        return `申請者の種別 ${supportedType}の条件付きルールが見つかりませんでした`;
      }
      for (const elem of this.accessor.applicantSection!.children) {
        if (
          ReservedApplicantIdHelper.isApplicantType(
            elem.id,
            this.applicantSchemaKeys,
          )
        ) {
          continue;
        }
        const correspondingRule = rule.then.rules.find((r) => r.id === elem.id);
        if (!correspondingRule) {
          return `申請者の種別 ${supportedType}の${elem.id}に対する条件付きルールが見つかりませんでした`;
        }
        if (
          ReservedApplicantIdHelper.isUnsupported(
            elem.id,
            supportedType,
            this.applicantSchemaKeys,
          )
        ) {
          // 当該申請者の種別でサポートしてはいけない入力要素の場合
          if (!correspondingRule.rule.hidden) {
            return `申請者の種別 ${supportedType}の${elem.id}が表示になっています`;
          }
          if (correspondingRule.rule.required) {
            return `申請者の種別 ${supportedType}の${elem.id}が必須になっています`;
          }
        }
      }
    }
    return;
  }

  /**
   * 申請者メールはすべて必須か非表示であること
   */
  applicantEmail() {
    if (
      !this.accessor.getApplicantElement(
        this.applicantSchemaKeys.applicantEmail.id,
      )
    ) {
      return;
    }
    const patterns = this.accessor.supportedApplicantTypes.map((tp) => {
      const applyingRule = this.accessor.getApplyingRuleFor(
        tp,
        this.applicantSchemaKeys.applicantEmail.id,
      )!;
      return decidePattern(applyingRule.rule);
    });
    if (new Set(patterns).size != 1) {
      return "申請者の種別によってメールアドレスの表示・非表示設定が異なります。";
    }
    if (
      patterns.find(
        (ptn) => ptn !== "REQUIRED_NOT_HIDDEN" && ptn !== "NOT_REQUIRED_HIDDEN",
      )
    ) {
      return "メールアドレスが、必須・非表示のいずれでもない状態に設定されています。";
    }
    return;
  }

  /**
   * 申請者セクションの要素が全てHiddenにならないこと
   */
  applicantSectionNotAllHidden() {
    const findNotHidden = (tp: ApplicantTypeLiterals) => {
      return this.accessor
        .applicantSectionConditionalRule(tp)
        .then.rules.filter(({ id }) => {
          const element = this.accessor.applicantSection?.children.find(
            (elem) => elem.id === id,
          );

          // ルールの対象が申請者セクションの element ではない場合の分岐。
          // 一部の個別プランで該当。
          // 従来の挙動を担保するために、検査対象とする。
          if (!element) {
            return true;
          }

          return isInputElement(element);
        })
        .find((e) => !e.rule.hidden);
    };
    for (const tp of this.accessor.supportedApplicantTypes) {
      if (!findNotHidden(tp)) {
        return `申請者の種別 [${tp}]の入力要素が全て非表示になっています`;
      }
    }
    return;
  }
  /**
   * マイナンバーカードの電子署名が必要な手続きに必須の要素が非表示にならないこと
   */
  myNumberSignatureRequiredElements() {
    const notHiddens = this.accessor
      .applicantSectionConditionalRule("INDIVIDUAL")
      .then.rules.filter((e) => !e.rule.hidden)
      .map((v) => v.id);
    for (const id of myNumberContainedAttributes(this.applicantSchemaKeys)) {
      if (!notHiddens.includes(id)) {
        return `マイナンバーカードの電子署名が必要な手続きに必須の要素「${toMyNumberContainAttrLabel(
          id,
          this.applicantSchemaKeys,
        )}」が非表示になっています`;
      }
    }
    return;
  }

  /**
   * 商業登記電子証明書による電子署名が必要な手続きに必須の要素が非表示にならないこと
   */
  mojSignatureRequiredElements() {
    const notHiddenFields = this.accessor
      .applicantSectionConditionalRule("CORPORATE")
      .then.rules.filter((e) => !e.rule.hidden)
      .map((v) => v.id);
    for (const id of mojCertificateContainedAttributes(
      this.applicantSchemaKeys,
    )) {
      if (!notHiddenFields.includes(id)) {
        return `商業登記電子証明書による電子署名が必要な手続きに必須の要素「${toMojContainAttrLabel(
          id,
          this.applicantSchemaKeys,
        )}」が非表示になっています`;
      }
    }
    return;
  }

  /**
   * 条件付きルールのユーザー設定部分のThenルールに、申請者セクションの要素がセットされていないこと
   */
  userAreaNotContainApplicantElements() {
    for (const userAreaRule of this.accessor.userAreaConditionalRule) {
      if (
        userAreaRule.then.rules.find((thenRule) =>
          ReservedApplicantIdHelper.contains(
            thenRule.id,
            this.applicantSchemaKeys,
          ),
        )
      ) {
        return "ユーザー領域の条件付きルールのTHEN文に申請者要素が設定されています";
      }
    }
    return;
  }

  private get accessor() {
    return new CloudGovFormSchemaAccessor(
      this.schema,
      this.applicantSchemaKeys,
    );
  }
}
